import { Box, Button, Flex, Spinner, Text, TextField, Tooltip, Switch, Card } from '@radix-ui/themes'
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Tables } from '@willie/supabase'
import { isEqual, pick } from 'lodash'
import { Suspense, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ActionablePageLayout } from '~/components/ActionablePageLayout'
import { ImageUpload } from '~/components/ImageUpload'
import { SectionCard } from '~/components/SectionCard'
import {
  getCurrentUserProfile,
  SimpleUserProfile,
  updateUserPassword,
  updateUserProfile
} from '~/features/auth/service'
import { useCurrentOrg } from '~/features/orgnisations/hooks/useCurrentOrg'
import { listSites } from '~/features/sites/service'
import { useTRPC } from '~/utils/trpc'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { OrgMemberNotificationPreferences, orgMemberNotificationPreferencesSchema } from '@willie/schemas'

const MIN_PASSWORD_LENGTH = 8

const pickProfileData = (profile: Tables<'user_profiles'>) => pick(profile, ['full_name', 'picture_url'])

// @ts-ignore
const NotificationSection = () => {
  const { id: orgId } = useCurrentOrg()
  const { queryClient } = Route.useRouteContext()
  const { t } = useTranslation('settings')
  const trpc = useTRPC()

  const {
    data: member,
    isLoading: notificationPreferencesLoading,
    error: memberError
  } = useQuery(trpc.orgMembers.getCurrentOrgMember.queryOptions())

  const {
    data: sites,
    isLoading: sitesLoading,
    error: sitesError
  } = useQuery({
    queryKey: ['sites', orgId],
    queryFn: () => listSites({ orgId }),
    enabled: !!orgId
  })

  const defaultValues = useMemo(() => {
    const prefs = member?.notificationPreferences
    // Ensure prefs conform to the schema or provide a default
    const parsedPrefs = orgMemberNotificationPreferencesSchema.safeParse(prefs)
    return parsedPrefs.success ? parsedPrefs.data : { mode: 'all' as const }
  }, [member])

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty }
  } = useForm<OrgMemberNotificationPreferences>({
    resolver: zodResolver(orgMemberNotificationPreferencesSchema),
    defaultValues
  })

  const mode = watch('mode')
  const subscriptions = watch('subscriptions')

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  const { mutate: updateNotificationPreferences, isPending: isUpdating } = useMutation({
    mutationFn: trpc.orgMembers.updateNotificationPreferences.mutationOptions().mutationFn,
    onSuccess: () => {
      toast.success(t('notifications.notificationUpdated'))
      queryClient.invalidateQueries({
        queryKey: trpc.orgMembers.getCurrentOrgMember.queryKey()
      })
    },
    onError: (err) => {
      toast.error(t('notifications.notificationUpdateError'))
      console.error('Failed to update notification preferences:', err)
    }
  })

  const isLoading = notificationPreferencesLoading || sitesLoading
  const problem = memberError || sitesError || !member || !sites

  return (
    <SectionCard title={t('notifications.sectionTitle')}>
      <Flex direction={'column'} gap="4">
        <Text size={'1'} color="gray">
          {t('notifications.sectionDescription')}
        </Text>
        {isLoading ? (
          <Flex align={'center'} justify={'center'} height={'9'} width={'full'}>
            <Spinner />
          </Flex>
        ) : problem ? (
          <Text size={'1'} color="red">
            {t('notifications.notificationError')} {memberError?.message || sitesError?.message}
          </Text>
        ) : (
          <form onSubmit={handleSubmit((preferences) => updateNotificationPreferences({ preferences }))}>
            <Flex direction="column" gap="4">
              <Box width={'full'}>
                <Card variant="surface" className="bg-mint-6" size={'2'}>
                  <Flex align="center" justify={'between'}>
                    <div>
                      <Text size="3" weight="bold" as="p" color="mint">
                        {t('notifications.allNotificationsTitle')}
                      </Text>
                      <Text size="2" color="gray" as="p">
                        {t('notifications.allNotificationsDescription')}
                      </Text>
                    </div>
                    <Controller
                      name="mode"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          defaultChecked={member.notificationPreferences.mode === 'all'}
                          onCheckedChange={(v) => field.onChange(v ? 'all' : 'custom')}
                        />
                      )}
                    />
                  </Flex>
                </Card>
              </Box>

              <Flex direction="column" gap="3">
                <Text size="2" mt="2" weight={'medium'}>
                  Sites
                </Text>
                <Text size="2" color="gray">
                  {t('notifications.selectSites')}
                </Text>
                {sites.map((site) => {
                  const idx = subscriptions?.findIndex((s) => s.scope === 'site' && s.id === site.id) ?? -1
                  const isChecked = idx !== -1
                  const disabled = mode === 'all' // Disable checkbox if mode is 'all'
                  return (
                    <Flex align={'center'} justify={'between'} key={site.id} px="4">
                      <Text size="3" weight={'bold'} className={disabled ? 'text-gray-8' : 'text-mint-11'}>
                        {site.name}
                      </Text>
                      <Switch
                        disabled={disabled}
                        checked={isChecked}
                        onCheckedChange={(checked) => {
                          const newSubscriptions = checked
                            ? [...(subscriptions ?? []), { scope: 'site' as const, id: site.id, topics: ['*'] }]
                            : subscriptions.filter((__, i) => i !== idx)

                          setValue('subscriptions', newSubscriptions, { shouldValidate: true, shouldDirty: true })
                        }}
                      />
                    </Flex>
                  )
                })}
              </Flex>

              <Box mt="3">
                <Button type="submit" disabled={!isDirty || isUpdating} loading={isUpdating}>
                  {t('notifications.saveButton')}
                </Button>
              </Box>
            </Flex>
          </form>
        )}
      </Flex>
    </SectionCard>
  )
}

const Page = () => {
  const { t } = useTranslation('settings')
  const { session, queryClient } = Route.useRouteContext()

  const [pwdState, setPwdState] = useState({
    reveal: false,
    password: '',
    confirmPassword: ''
  })

  const canUpdatePwd = useMemo(() => {
    return (
      pwdState.password.length >= MIN_PASSWORD_LENGTH &&
      /[a-z]/.test(pwdState.password) &&
      /[A-Z]/.test(pwdState.password) &&
      /[0-9]/.test(pwdState.password) &&
      /[^A-Za-z0-9]/.test(pwdState.password) &&
      pwdState.password === pwdState.confirmPassword
    )
  }, [pwdState.password, pwdState.confirmPassword])

  const { mutate: updatePwd, isPending: updatePwdPending } = useMutation({
    mutationFn: () => updateUserPassword(pwdState.password),
    onSuccess: () => {
      setPwdState({ reveal: false, password: '', confirmPassword: '' })
      queryClient.invalidateQueries({
        queryKey: ['profile']
      })
      toast.success(t('profile.passwordUpdated'))
    },
    onError: () => {
      toast.error(t('profile.passwordUpdateError'))
    }
  })

  const [profileData, setProfileData] = useState<SimpleUserProfile | null>(null)

  const { data: userProfile } = useSuspenseQuery({
    queryFn: () => getCurrentUserProfile({ userID: session.user.id }),
    queryKey: ['profile']
  })

  useEffect(() => {
    if (userProfile) {
      setProfileData(pickProfileData(userProfile))
    }
  }, [userProfile])

  const isProfileDirty = useMemo(() => {
    if (!userProfile || !profileData) {
      return false
    }
    return !isEqual(profileData, pickProfileData(userProfile))
  }, [userProfile, profileData])

  const { mutate: updateProfile, isPending: updateProfilePending } = useMutation({
    mutationFn: async () => !!profileData && updateUserProfile(session.user.id, profileData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['profile']
      })
      toast.success(t('profile.profileUpdated'))
    },
    onError: () => {
      toast.error(t('profile.profileUpdateError'))
    }
  })

  return (
    <ActionablePageLayout title={t('profile.pageTitle')}>
      <Flex m={'auto'} gap="5" direction={'column'} className="w-full lg:w-[90%] xl:w-[70%] max-w-[1200px]">
        <Suspense
          fallback={
            <SectionCard title="" className="min-h-[300px]">
              <Flex align={'center'} justify={'center'} height={'full'} width={'full'}>
                <Spinner />
              </Flex>
            </SectionCard>
          }
        >
          <SectionCard title={t('profile.profileInfo')}>
            <Box>
              <Text size={'2'}>{t('profile.profilePicture')}</Text>
              <ImageUpload
                size={100}
                imageUrl={userProfile?.picture_url}
                uploadKey={`${session.user.id}/profile-picture/${Date.now()}`}
                onUploadSuccess={(picture_url) => {
                  setProfileData((d) => (d === null ? null : { ...d, picture_url }))
                }}
              />
              <Text size={'1'} color="gray">
                {t('profile.profilePictureHint')}
              </Text>
            </Box>
            <Box>
              <Text size={'2'}>{t('profile.fullName')}</Text>
              <TextField.Root
                my="3"
                value={profileData?.full_name ?? ''}
                onChange={(e) => setProfileData((d) => (d === null ? null : { ...d, full_name: e.target.value }))}
              />
              <Button disabled={!isProfileDirty} onClick={() => updateProfile()} loading={updateProfilePending}>
                {t('general.saveButton')}
              </Button>
            </Box>
          </SectionCard>
          {/* <NotificationSection /> */}
        </Suspense>
        <SectionCard title={t('profile.loginInfo')}>
          <Flex direction={'column'} gap="4">
            <Flex direction={'column'} gap="1">
              <Text size={'2'}>{t('profile.email')}</Text>
              <Tooltip content={t('profile.emailNotEditable')}>
                <TextField.Root disabled my="3" value={session.user.email} />
              </Tooltip>
            </Flex>
            <Flex direction={'column'} gap={'3'}>
              <Text size={'2'}>{t('profile.password')}</Text>
              <Text size={'1'} color="gray">
                {t('profile.passwordHint', { minLength: MIN_PASSWORD_LENGTH })}
              </Text>

              <TextField.Root
                placeholder={t('profile.passwordPlaceholder')}
                type={pwdState.reveal ? 'text' : 'password'}
                value={pwdState.password}
                onChange={(e) => {
                  setPwdState((s) => ({ ...s, password: e.target.value }))
                }}
              >
                <TextField.Slot side="right">
                  <Button
                    variant="soft"
                    size="1"
                    color="gray"
                    onClick={() => {
                      setPwdState((s) => ({ ...s, reveal: !s.reveal }))
                    }}
                  >
                    {pwdState.reveal ? t('profile.hidePassword') : t('profile.showPassword')}
                  </Button>
                </TextField.Slot>
              </TextField.Root>
              <TextField.Root
                placeholder={t('profile.confirmPassword')}
                value={pwdState.confirmPassword}
                type={pwdState.reveal ? 'text' : 'password'}
                onChange={(e) => {
                  setPwdState((s) => ({ ...s, confirmPassword: e.target.value }))
                }}
                disabled={!pwdState.password}
              />
              <Box mt="3">
                <Button disabled={!canUpdatePwd} onClick={() => updatePwd()} loading={updatePwdPending}>
                  {t('profile.updatePassword')}
                </Button>
              </Box>
            </Flex>
          </Flex>
        </SectionCard>
      </Flex>
    </ActionablePageLayout>
  )
}

export const Route = createFileRoute('/profile')({
  component: Page
})
